import React, { memo } from 'react'
import { Image, ImageStyle, View } from 'react-native'
import { useNavigation } from '@react-navigation/native'
import { StackNavigationProp } from '@react-navigation/stack'
import { Feature, useFeatureFlag } from '@src/components'
import { StyleService, useStyleSheet } from '@src/style/service'
import { Text, Button, Icon } from '@components/base'
import { useNotificationBanner } from '@src/components/notifications/utils/hooks'
import {
  NotificationEngineNotificationKind,
  NotificationEngineNotificationStyle,
  NotificationEngineScreen,
} from '@src/types'
import { AppStackParamList } from '@src/navigation/types'
import { cgmSteloImage } from '@src/assets/images'
import { ChatlikeNotificationBanner } from './ChatlikeNotificationBanner'
import { ExpirationNotificationBanner } from './ExpirationNotificationBanner'
import { IntegrationPromotionNotificationBanner } from './IntegrationPromotionNotificationBanner'

interface NotificationBannerProps {
  screen: NotificationEngineScreen
}

const STELO_NOTIFICATIONS_KINDS = [
  NotificationEngineNotificationKind.VideoCallSteloUpsell,
  NotificationEngineNotificationKind.LastSteloSensorAboutToExpire,
  NotificationEngineNotificationKind.LastSteloSensorAboutToExpireForDexcomUser,
]

export const NotificationBanner = memo(({ screen }: NotificationBannerProps) => {
  const notificationInboxEnabled = useFeatureFlag(Feature.NotificationInbox)
  const styles = useStyleSheet(themedStyles)
  const data = useNotificationBanner(screen)
  const navigation = useNavigation<StackNavigationProp<AppStackParamList>>()

  if (notificationInboxEnabled) {
    return null
  }

  if (!data) {
    return null
  }

  const {
    iconName,
    content,
    action,
    actionButtonLabel,
    dismissButtonLabel,
    dismiss,
    style,
    kind,
    payload,
  } = data

  const isSteloNotification = STELO_NOTIFICATIONS_KINDS.includes(kind)

  if (style === NotificationEngineNotificationStyle.Expiration) {
    return (
      <ExpirationNotificationBanner
        content={content}
        actionButtonLabel={actionButtonLabel}
        dismissButtonLabel={dismissButtonLabel}
        dismiss={dismiss}
        action={action}
        payload={payload}
        iconName={iconName}
        isSteloNotification={isSteloNotification}
      />
    )
  }

  if (style === NotificationEngineNotificationStyle.Chatlike) {
    const isLabResultsNotification = [
      NotificationEngineNotificationKind.SubmitLabResultsCustomDate,
      NotificationEngineNotificationKind.SubmitLabResultsPeriodical,
    ].includes(kind)

    const dismissHandler = () => {
      dismiss()
      if (isLabResultsNotification) {
        navigation.navigate('SetReminderModal', {
          dismissButtonText: 'Dismiss Notification',
          goBackOnDismiss: false,
        })
      }
    }

    return (
      <ChatlikeNotificationBanner
        action={action}
        actionButtonLabel={actionButtonLabel}
        content={content}
        dismiss={dismissHandler}
      />
    )
  }

  if (style === NotificationEngineNotificationStyle.IntegrationPromotion) {
    return (
      <IntegrationPromotionNotificationBanner
        content={content}
        actionButtonLabel={actionButtonLabel}
        dismissButtonLabel={dismissButtonLabel}
        dismiss={dismiss}
        action={action}
        payload={payload}
        iconName={iconName}
      />
    )
  }

  return (
    <View style={styles.banner}>
      <View style={styles.contentWrapper}>
        {isSteloNotification ? (
          <Image source={cgmSteloImage.imageSource} style={styles.image as ImageStyle} />
        ) : (
          <Icon name={iconName} style={styles.icon} />
        )}
        <Text type="regular" style={styles.content}>
          {content}
        </Text>
      </View>
      <View style={styles.buttonWrapper}>
        <Button
          type="transparent"
          size="small"
          accessibilityLabel={dismissButtonLabel}
          style={styles.button}
          onPress={dismiss}
        >
          {dismissButtonLabel}
        </Button>
        {action && (
          <Button
            type="primary"
            size="small"
            accessibilityLabel={actionButtonLabel}
            style={styles.button}
            onPress={action}
          >
            {actionButtonLabel}
          </Button>
        )}
      </View>
    </View>
  )
})

const themedStyles = StyleService.create({
  banner: {
    margin: 16,
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderRadius: 8,
    borderWidth: 2,
    borderColor: 'theme.surface.base1',
  },
  contentWrapper: {
    flexDirection: 'row',
    marginBottom: 12,
  },
  icon: {
    color: 'theme.primary.base',
    marginRight: 16,
  },
  iconDanger: {
    color: 'theme.error.base',
  },
  content: {
    flex: 1,
  },
  buttonWrapper: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
  button: {
    marginLeft: 8,
  },
  image: {
    width: 48,
    height: 48,
    marginRight: 16,
  },
})
